import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ScreenShell } from '../shell/ScreenShell';
import { Card } from '../design-system/Card';
import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { useSessionStore } from '../../stores/sessionStore';
import { analytics } from '../../lib/analytics';
import { formatCurrency } from '../../utils/format';
import { Wallet, Check, IndianRupee } from 'lucide-react';

const PRESETS = [100, 200, 300, 500];

export function FastagRechargeScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/challan-listing';
  const source = searchParams.get('source') || 'unknown';
  const balanceParam = searchParams.get('balance');
  const recommendedParam = searchParams.get('recommended_amount');

  const completeRecharge = useSessionStore((s) => s.completeRecharge);
  const abandonRecharge = useSessionStore((s) => s.abandonRecharge);

  const balance = balanceParam ? Number(balanceParam) : 0;
  const recommended = recommendedParam ? Number(recommendedParam) : 200;
  const [amount, setAmount] = useState(recommended);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAmountValid = !Number.isNaN(amount) && amount > 0;

  useEffect(() => {
    analytics.emit('recharge_screen_view', { source, balance, recommended });
  }, [source, balance, recommended]);

  const handleComplete = () => {
    if (!isAmountValid) {
      setError('Please enter a valid amount greater than ₹0.');
      return;
    }
    setError(null);
    analytics.emit('recharge_completed', { source, amount, previous_balance: balance });
    completeRecharge();
    setCompleted(true);
    setTimeout(() => {
      navigate(returnTo);
    }, 1200);
  };

  const handleCancel = () => {
    analytics.emit('recharge_abandoned', { source, amount });
    abandonRecharge();
    navigate(returnTo);
  };

  return (
    <ScreenShell title="FASTag Recharge" showBack onBack={handleCancel}>
      <div className="flex flex-col gap-4 p-4">
        <Card padding="md" elevation={2} className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-park-50 text-park-600 dark:bg-park-900/30 dark:text-park-400">
            <Wallet className="h-7 w-7" />
          </div>
          <Text variant="caption" color="secondary" className="mt-3">
            Current FASTag balance
          </Text>
          <Text variant="display-large" color={balance < 0 ? 'error' : 'primary'} className="mt-1">
            {formatCurrency(balance)}
          </Text>
          <Text variant="caption" color="secondary" className="mt-1">
            Recommended top-up
          </Text>
          <Text variant="title" color="primary-brand">
            {formatCurrency(recommended)}
          </Text>
        </Card>

        <Card padding="md" elevation={1}>
          <Text variant="body-medium">Choose amount</Text>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setAmount(preset)}
                className={`flex flex-col items-center justify-center rounded-xl border p-3 transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
                  amount === preset
                    ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
                }`}
              >
                <IndianRupee className="h-3 w-3" />
                <span className="text-sm font-semibold">{preset}</span>
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
            <IndianRupee className="h-4 w-4 text-slate-400" />
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                setError(null);
                setAmount(Number(e.target.value));
              }}
              className="w-full bg-transparent text-base font-medium outline-none"
              aria-label="Custom recharge amount"
            />
          </div>
          {error && (
            <Text variant="caption" color="error" className="mt-2">
              {error}
            </Text>
          )}
        </Card>
      </div>

      <div className="mt-auto p-4 pb-6">
        {completed ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex items-center justify-center gap-2 rounded-xl bg-emerald-50 py-4 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
          >
            <Check className="h-5 w-5" />
            <span className="font-medium">Recharge successful</span>
          </motion.div>
        ) : (
          <>
            <Button size="lg" fullWidth onClick={handleComplete} className="mb-3">
              Recharge {formatCurrency(amount)}
            </Button>
            <Button size="lg" variant="outline" fullWidth onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </ScreenShell>
  );
}
